/**
 * Campaign Asset Service
 * Manages channel content/posts for campaigns
 */

import { NodeAxios } from '../api'

export const campaignAssetService = {
    /**
     * Create a new asset
     */
    createAsset: async (campaignId: string, assetData: any): Promise<any> => {
        const result = await NodeAxios.post(`/campaigns/${campaignId}/assets`, assetData)
        return result.data || result
    },

    /**
     * Get asset by ID
     */
    getAsset: async (campaignId: string, assetId: string): Promise<any> => {
        const result = await NodeAxios.get(`/campaigns/${campaignId}/assets/${assetId}`)
        return result.data || result
    },

    /**
     * List assets for a campaign
     */
    listAssets: async (
        campaignId: string,
        filters?: {
            beat?: string
            channel?: string
            status?: string
            asset_type?: string
        }
    ): Promise<{ assets: any[]; total: number }> => {
        const params = new URLSearchParams()
        if (filters?.beat) params.append('beat', filters.beat)
        if (filters?.channel) params.append('channel', filters.channel)
        if (filters?.status) params.append('status', filters.status)
        if (filters?.asset_type) params.append('asset_type', filters.asset_type)

        const result = await NodeAxios.get(`/campaigns/${campaignId}/assets?${params.toString()}`)
        return result.data || result || { assets: [], total: 0 }
    },

    /**
     * List assets for a specific beat
     */
    listBeatAssets: async (
        campaignId: string,
        beatId: string,
        channel?: string
    ): Promise<{ assets: any[]; total: number }> => {
        const params = channel ? `?channel=${channel}` : ''
        const result = await NodeAxios.get(`/campaigns/${campaignId}/beats/${beatId}/assets${params}`)
        return result.data || result || { assets: [], total: 0 }
    },

    /**
     * Update asset
     */
    updateAsset: async (campaignId: string, assetId: string, updateData: any): Promise<any> => {
        const result = await NodeAxios.patch(`/campaigns/${campaignId}/assets/${assetId}`, updateData)
        return result.data || result
    },

    /**
     * Delete asset
     */
    deleteAsset: async (campaignId: string, assetId: string, hard: boolean = false): Promise<boolean> => {
        const params = hard ? '?hard=true' : ''
        const result = await NodeAxios.delete(`/campaigns/${campaignId}/assets/${assetId}${params}`)
        return result.data?.success || false
    },

    /**
     * Get asset statistics
     */
    getAssetStats: async (campaignId: string): Promise<any> => {
        const result = await NodeAxios.get(`/campaigns/${campaignId}/assets/stats`)
        return result.data || result
    },

    /**
     * Bulk create assets
     */
    bulkCreateAssets: async (campaignId: string, assets: any[]): Promise<{ assets: any[]; total: number }> => {
        const result = await NodeAxios.post(`/campaigns/${campaignId}/assets/bulk`, { assets })
        return result.data || result || { assets: [], total: 0 }
    },

    /**
     * Update asset status
     */
    updateAssetStatus: async (campaignId: string, assetId: string, status: string): Promise<any> => {
        const result = await NodeAxios.patch(`/campaigns/${campaignId}/assets/${assetId}/status`, { status })
        return result.data || result
    },

    /**
     * Trigger background draft content generation
     */
    triggerDraftGeneration: async (
        campaignId: string,
        beatId: string,
        type: 'strategy' | 'outline' | 'post',
        channel?: string
    ): Promise<any> => {
        const result = await NodeAxios.post(`/campaigns/${campaignId}/beats/${beatId}/draft-content`, { type, channel })
        return result.data || result
    },

    /**
     * Claim a prebuilt strategy package (strategy + outlines + posts)
     */
    claimPrebuiltPackage: async (campaignId: string, beatId: string, strategyId: string): Promise<any> => {
        const result = await NodeAxios.post(
            `/campaigns/${campaignId}/beats/${beatId}/prebuilt-content/${strategyId}/claim`
        )
        return result.data || result
    },

    /**
     * Generate topic ideas for a channel using strategic context
     */
    generateChannelTopics: async (
        campaignId: string,
        beatId: string,
        channelId: string,
        strategicContext: {
            target_id?: string
            goal_ids?: string[]
            alo_ids?: string[]
            problem_ids?: string[]
            solution_ids?: string[]
            strategy_text?: string
            tone?: string
            story_narrative?: string
            research_notes?: string
            dial_in?: {
                mundane_elements: string[]
                exciting_elements: string[]
                emotional_hooks: string[]
                key_tensions: string[]
            }
            count?: number
            additional_context?: string
            library_story_context?: string
        }
    ): Promise<{ topics: any[] }> => {
        const result = await NodeAxios.post(
            `/campaigns/${campaignId}/beats/${beatId}/channels/${channelId}/generate-topics`,
            strategicContext
        )
        return result.data || result || { topics: [] }
    },

    /**
     * Generate visual prompts (Step 0 "Visual Scent") for multiple topics in bulk
     */
    generateBulkVisualPrompts: async (
        campaignId: string,
        payload: {
            beatId?: string
            topicIds?: string[]
            scope?: 'current' | 'first-three' | 'all'
        }
    ): Promise<{ success: boolean; message: string; count: number }> => {
        const result = await NodeAxios.post(`/campaigns/${campaignId}/topics/visual-prompts`, payload)
        return result.data || result
    },
    
    /**
     * Generate content for posts that don't have content yet
     * Optionally includes visual generation
     */
    generateBeatContent: async (
        campaignId: string,
        payload: {
            beatId: string
            includeVisuals?: boolean
        }
    ): Promise<{ success: boolean; message: string; count: number }> => {
        const result = await NodeAxios.post(`/campaigns/${campaignId}/beats/${payload.beatId}/generate-content`, {
            includeVisuals: payload.includeVisuals || false
        })
        return result.data || result
    },
}
